import { Journey } from "../api/types";

import CONFIG from "../config.ts";
const colours = CONFIG.colours;

export interface Disc {
  lat: number;
  lng: number;
  // Metres.
  radius: number;
  colour: string;
}

// ~130m north-south; stations closer together than this paint nearly
// identical discs, so keeping only the fastest per cell is invisible on the
// map but cuts the disc count several-fold (London bus stops are dense).
const THINNING_GRID_DEG = 0.0012;

const thin = (journeys: Journey[]): Journey[] => {
  const fastestPerCell = new Map<string, Journey>();
  for (const journey of journeys) {
    const key =
      Math.round(journey.destination.location.x / THINNING_GRID_DEG) +
      ":" +
      Math.round(journey.destination.location.y / THINNING_GRID_DEG);
    const current = fastestPerCell.get(key);
    if (!current || journey.minutes < current.minutes) {
      fastestPerCell.set(key, journey);
    }
  }
  return [...fastestPerCell.values()];
};

// One band per colour, evenly spaced up to maxMinutes: each band paints,
// for every station reachable within its threshold, the area you can
// additionally cover on foot — slowest band first so faster areas paint on
// top. Returns plain disc data in draw order; HeatCanvas renders it.
const makeDiscs = (journeys: Journey[], maxMinutes: number): Disc[] => {
  if (journeys.length === 0) {
    return [];
  }

  const thinned = thin(journeys);

  // Always show at least a little around the fastest station, even when the
  // slider is below the journey time to the nearest station.
  const shortest = Math.min(...thinned.map((j) => j.minutes));
  const maxMins = Math.max(maxMinutes, shortest + 10);

  const discs: Disc[] = [];
  for (let band = colours.length - 1; band >= 0; band--) {
    const threshold =
      shortest + ((band + 1) / colours.length) * (maxMins - shortest);
    for (const journey of thinned) {
      if (journey.minutes < threshold) {
        discs.push({
          lat: journey.destination.location.x,
          lng: journey.destination.location.y,
          radius:
            CONFIG.walkingSpeed * (threshold - journey.minutes + 0.5),
          colour: colours[band],
        });
      }
    }
  }

  return discs;
};

export default makeDiscs;
